import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import {
  VOUCHER_TEXT_MODES,
  VoucherTextIssue,
  VoucherTextLanguage,
  VoucherTextMode,
  VoucherTextScope,
} from '@bandido/shared/types';

import {
  VoucherConnectorRead,
  VoucherProviderRow,
  VoucherTextsDraft,
  voucherProviderRows,
  voucherTextLimit,
} from '../voucherTexts';
import { TextBlockBox } from './TextBlockBox';

const KEY = 'vouchers.config';

interface ProvidersSectionProps {
  /** El catálogo del hub para la agencia, configurados o no. */
  connectors?: VoucherConnectorRead[];
  connectorsLoading: boolean;
  connectorsFailed: boolean;
  onRetryConnectors: () => void;
  draft: VoucherTextsDraft;
  /** Lo guardado: un proveedor con texto guardado se puede vaciar aunque ya no esté configurado. */
  saved: VoucherTextsDraft;
  limits?: Record<string, number>;
  issues: Record<string, VoucherTextIssue[]>;
  /** Los proveedores que el `PUT` rechazó por no estar configurados en el hub. */
  sinConfigurar: string[];
  otrosRechazados?: boolean;
  canEdit: boolean;
  disabledReason?: string;
  /** La clave del proveedor abierto, o ninguna. La maneja la pantalla porque mueve la vista previa. */
  open?: string;
  onOpen: (row: VoucherProviderRow | undefined) => void;
  onChange: (scope: VoucherTextScope, key: string, lang: VoucherTextLanguage, body: string) => void;
  onMode: (key: string, mode: VoucherTextMode) => void;
}

/**
 * POR PROVEEDOR: un texto para las reservas de un proveedor, que se agrega
 * debajo del de su tipo de producto o lo sustituye.
 *
 * Una fila por CONECTOR del catálogo del hub (W3): un proveedor con dos tipos de
 * producto son dos filas, porque son dos textos. Los no configurados se ven
 * apagados con su motivo, nunca se esconden: la lista responde también "¿por qué
 * no puedo escribirle a este?".
 */
export const ProvidersSection: FC<ProvidersSectionProps> = ({
  connectors,
  connectorsLoading,
  connectorsFailed,
  onRetryConnectors,
  draft,
  saved,
  limits,
  issues,
  sinConfigurar,
  otrosRechazados,
  canEdit,
  disabledReason,
  open,
  onOpen,
  onChange,
  onMode,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const rows = voucherProviderRows({ connectors, draft, saved, issues, sinConfigurar });

  return (
    <Stack gap={2}>
      <Alert severity="info">{t(`${KEY}.byProvider.appliesTo`)}</Alert>

      {!canEdit && disabledReason && <Alert severity="warning">{t(disabledReason)}</Alert>}
      {otrosRechazados && <Alert severity="error">{t(`${KEY}.texts.otherRejected`)}</Alert>}

      {connectorsLoading && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">{t(`${KEY}.byProvider.loading`)}</Typography>
        </Box>
      )}
      {/* Sin la lista del hub no hay filas: se dice, y se puede reintentar sin
          perder lo que haya escrito en otras anclas. */}
      {connectorsFailed && (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={onRetryConnectors}>
              {t(`${KEY}.byProvider.retry`)}
            </Button>
          }
        >
          {t(`${KEY}.byProvider.loadFailed`)}
        </Alert>
      )}
      {!connectorsLoading && !connectorsFailed && rows.length === 0 && (
        <Alert severity="info">{t(`${KEY}.byProvider.empty`)}</Alert>
      )}

      <Stack gap={1}>
        {rows.map((row) => {
          const abierto = open === row.key;
          const editable = canEdit && row.editable;
          return (
            <Box
              key={row.key}
              sx={{
                border: `1px solid ${row.hasIssues ? theme.palette.error.main : theme.palette.divider}`,
                borderRadius: '12px',
                padding: theme.spacing(1.5, 2),
                opacity: row.editable ? 1 : 0.6,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                sx={{ cursor: 'pointer' }}
                onClick={() => onOpen(abierto ? undefined : row)}
              >
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle2">
                    {row.vertical ? `${row.connector} · ${t(`rutero.verticals.${row.vertical}`)}` : row.connector}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={row.rejectedAsUnknown ? 'error' : 'text.secondary'}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {/* El rechazo del hub se dice también con la fila cerrada: el
                        borde rojo solo no explica qué pasó. */}
                    {row.rejectedAsUnknown
                      ? t(`${KEY}.byProvider.rejectedShort`)
                      : row.own
                        ? row.preview
                        : t(`${KEY}.byProvider.noText`)}
                  </Typography>
                </Box>
                {!row.configured && (
                  <Chip size="small" variant="outlined" label={t(`${KEY}.byProvider.notConfigured`)} />
                )}
                {row.own && (
                  <Chip size="small" color="primary" label={t(`${KEY}.byProvider.${row.mode}`)} />
                )}
                <IconButton size="small" aria-label={t(`${KEY}.byProvider.${abierto ? 'close' : 'edit'}`)}>
                  {abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={abierto} unmountOnExit>
                <Stack gap={2} pt={2}>
                  {row.rejectedAsUnknown && (
                    <Alert severity="error">{t(`${KEY}.byProvider.rejectedAsUnknown`)}</Alert>
                  )}
                  <TextBlockBox
                    scope="connector"
                    blockKey={row.key}
                    draft={draft}
                    limit={voucherTextLimit(limits, 'connector', row.key)}
                    issues={issues}
                    canEdit={editable}
                    hint={t(`${KEY}.byProvider.hint`)}
                    onChange={onChange}
                  />
                  <Box>
                    {/* Sin texto no hay bloque que lleve el modo, y en un proveedor que
                        el hub no tiene configurado solo se puede vaciar: en los dos
                        casos elegirlo no se guardaría. */}
                    {/* Con modos distintos por idioma no se marca ninguno: elegir
                        uno lo fija en los dos. */}
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={row.mode === 'mixed' ? null : row.mode}
                      disabled={!canEdit || !row.modeSelectable}
                      onChange={(_event, value) => value && onMode(row.key, value as VoucherTextMode)}
                    >
                      {VOUCHER_TEXT_MODES.map((mode) => (
                        <ToggleButton key={mode} value={mode}>
                          {t(`${KEY}.byProvider.${mode}`)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {t(row.modeHelpKey)}
                    </Typography>
                  </Box>
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};
